// src/components/Pages/Dashboard/Dashboard.jsx
import React, { useState, useEffect } from "react";
import "./Dashboard.scss";
import SmallCard from "../../common/smallcards/SmallCard";
import NotificationCard from "./NotificationCard";
import RecentActivityCard from "./RecentActivityCard";
import apiClient from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";

const defaultDashboardData = {
  staffsActive: 24,
  staffsInactive: 3,
  studiesActive: 5,
  studiesInactive: 1,
  participantsActive: 142,
  participantsInactive: 8,
  appointmentsConfirmed: 12,
  appointmentsArrived: 8,
  appointmentsCancelled: 2,
  appointmentsDidNotAttend: 1,
  eisfPsfActive: 45,
  eisfPsfArchived: 12,
  qmsActive: 28,
  qmsArchived: 6,
};

// Default notifications; backend sends formatted datetime (dd/MM/yyyy hh:mm tt)
const defaultNotifications = [
  { id: 1, message: "You have new document from user approval", timestamp: "03/02/2025 10:30 AM", senderName: "Anderson Jacob", isRead: false },
  { id: 2, message: "You have new document from user review", timestamp: "03/02/2025 09:45 AM", senderName: "Hassan Ahmed", isRead: false },
  { id: 3, message: "You have new document from user review. This is a longer message used to test the Show more and Show less feature in the notification card.", timestamp: "02/02/2025 04:15 PM", senderName: "Hassan Ahmed", isRead: false },
  { id: 4, message: "Dear user you have new document change request", timestamp: "02/02/2025 11:20 AM", senderName: "Alex Morgan", isRead: false },
  { id: 5, message: "Protocol amendment has been submitted for review", timestamp: "02/02/2025 09:00 AM", senderName: "Sarah Lee", isRead: false },
  { id: 6, message: "ICF document signed and uploaded", timestamp: "01/02/2025 05:45 PM", senderName: "Mike Chen", isRead: false },
  { id: 7, message: "Lab results updated for Site 102", timestamp: "01/02/2025 03:20 PM", senderName: "Emily Brown", isRead: false },
  { id: 8, message: "Safety report Q4 approved by medical monitor", timestamp: "01/02/2025 11:15 AM", senderName: "James Wilson", isRead: false },
  { id: 9, message: "New comment on Section 5.2 – please review", timestamp: "31/01/2025 04:30 PM", senderName: "Linda Wu", isRead: false },
  { id: 10, message: "Site qualification form received", timestamp: "31/01/2025 02:10 PM", senderName: "David Kim", isRead: false },
  { id: 11, message: "Adverse event report requires follow-up", timestamp: "31/01/2025 10:00 AM", senderName: "Anna Patel", isRead: false },
  { id: 12, message: "Budget amendment request submitted", timestamp: "30/01/2025 03:55 PM", senderName: "Robert Taylor", isRead: false },
  { id: 13, message: "Regulatory submission package ready for review", timestamp: "30/01/2025 09:30 AM", senderName: "Jennifer Martinez", isRead: false },
  { id: 14, message: "Investigator brochure updated to version 3.1", timestamp: "29/01/2025 04:00 PM", senderName: "Chris Evans", isRead: false },
  { id: 15, message: "Patient consent form approved for all sites", timestamp: "29/01/2025 11:45 AM", senderName: "Maria Garcia", isRead: false },
];

function Dashboard() {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const [dashboardData, setDashboardData] = useState(defaultDashboardData);
  const [notifications, setNotifications] = useState(defaultNotifications);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = async () => {
    if (!userData) return;
    setLoading(true);
    setError(null);
    try {
      showLoader("Loading dashboard...");
      const response = await apiClient.post(
        API_ENDPOINTS.ISF.GET_DASHBOARD_DATA,
        {
          OrganizationId: userData.OrganizationId,
          ActionType: "GetLandingDashboard",
          CreatedBy: userData.StaffId,
          StudyId: "",
        },
      );

      if (response?.data?.CountTable) {
        const ct = response.data.CountTable[0] || {};
        setDashboardData({
          staffsActive: ct.ActiveStaff ?? defaultDashboardData.staffsActive,
          staffsInactive: ct.InactiveStaff ?? defaultDashboardData.staffsInactive,
          studiesActive: ct.ActiveStudies ?? defaultDashboardData.studiesActive,
          studiesInactive: ct.InactiveStudies ?? defaultDashboardData.studiesInactive,
          participantsActive: ct.ParticipantsActive ?? defaultDashboardData.participantsActive,
          participantsInactive: ct.ParticipantsInactive ?? defaultDashboardData.participantsInactive,
          appointmentsConfirmed: ct.AppointmentsConfirmed ?? defaultDashboardData.appointmentsConfirmed,
          appointmentsArrived: ct.AppointmentsArrived ?? defaultDashboardData.appointmentsArrived,
          appointmentsCancelled: ct.AppointmentsCancelled ?? defaultDashboardData.appointmentsCancelled,
          appointmentsDidNotAttend: ct.AppointmentsDidNotAttend ?? defaultDashboardData.appointmentsDidNotAttend,
          eisfPsfActive: ct.Documents ?? defaultDashboardData.eisfPsfActive,
          eisfPsfArchived: ct.ArchivedDocuments ?? defaultDashboardData.eisfPsfArchived,
          qmsActive: ct.QMSActiveDocuments ?? defaultDashboardData.qmsActive,
          qmsArchived: ct.QMSInActiveDocuments ?? defaultDashboardData.qmsArchived,
        });
      }
      // Map API notifications if provided; otherwise keep default notifications
      const rawNotifications = response?.data?.Notifications ?? response?.data?.notifications ?? [];
      if (Array.isArray(rawNotifications) && rawNotifications.length > 0) {
        setNotifications(
          rawNotifications.map((n, i) => ({
            id: n.id ?? n.NotificationId ?? `n-${i}`,
            title: n.title ?? n.ActionName ?? "Notification",
            message: n.message ?? n.Message ?? n.NotificationBody ?? "",
            timestamp: n.timestamp ?? n.Timestamp ?? n.CreatedDate ?? n.SystemDateTime ?? Date.now(),
            isRead: n.isRead ?? n.IsRead ?? false,
            senderName: n.senderName ?? n.SenderName ?? n.CreatedBy ?? n.CommentedByName,
            avatar: n.avatar ?? n.Avatar,
          }))
        );
      }
      
      setRecentActivity(response?.data?.RecentActivity ?? []);
    } catch (err) {
      setError(err.message || "Failed to load dashboard");
      setDashboardData(defaultDashboardData);
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [userData]);

  const formatValue = (val) =>
    typeof val === "number" ? val.toLocaleString() : String(val ?? "0");

  return (
    <div className="dashboard container-fluid">
      {error && (
        <div className="alert alert-warning mb-3" role="alert">
          {error}
        </div>
      )}

      <div className="row g-3 mb-3">
        <div className="col-6 col-md-3">
          <SmallCard
            title="Staffs"
            value={loading ? "—" : `${formatValue(dashboardData.staffsActive)} / ${formatValue(dashboardData.staffsInactive)}`}
            subtitle="Active / Inactive"
            backgroundColor="#f8fafc"
            iconBgColor="#d1fae5"
            iconColor="#198754"
            fontColor="#1e293b"
            height="110px"
            icon="fa fa-users"
          />
        </div>
        <div className="col-6 col-md-3">
          <SmallCard
            title="Studies"
            value={loading ? "—" : `${formatValue(dashboardData.studiesActive)} / ${formatValue(dashboardData.studiesInactive)}`}
            subtitle="Active / Inactive"
            backgroundColor="#f8fafc"
            iconBgColor="#ede9fe"
            iconColor="#6f42c1"
            fontColor="#1e293b"
            height="110px"
            icon="fa fa-flask"
          />
        </div>
        {/* <div className="col-6 col-md-2">
          <SmallCard
            title="Participants"
            value={loading ? "—" : `${formatValue(dashboardData.participantsActive)} / ${formatValue(dashboardData.participantsInactive)}`}
            subtitle="Active / Inactive"
            backgroundColor="#f8fafc"
            iconBgColor="#d1fae5"
            iconColor="#059669"
            fontColor="#1e293b"
            height="110px"
            icon="fa fa-user-friends"
          />
        </div>
        <div className="col-6 col-md-2">
          <SmallCard
            title="Appointments"
            value={loading ? "—" : `${formatValue(dashboardData.appointmentsConfirmed)} / ${formatValue(dashboardData.appointmentsArrived)} / ${formatValue(dashboardData.appointmentsCancelled)} / ${formatValue(dashboardData.appointmentsDidNotAttend)}`}
            subtitle="Conf. / Arrived / Canc. / DNA"
            backgroundColor="#f8fafc"
            iconBgColor="#fce7f3"
            iconColor="#be185d"
            fontColor="#1e293b"
            height="110px"
            icon="fa fa-calendar-check"
          />
        </div> */}
        <div className="col-6 col-md-3">
          <SmallCard
            title="Eisf/Psf Documents"
            value={loading ? "—" : `${formatValue(dashboardData.eisfPsfActive)} / ${formatValue(dashboardData.eisfPsfArchived)}`}
            subtitle="Active / Archived"
            backgroundColor="#f8fafc"
            iconBgColor="#dbeafe"
            iconColor="#0d6efd"
            fontColor="#1e293b"
            height="110px"
            icon="fa fa-folder-open"
          />
        </div>
        <div className="col-6 col-md-3">
          <SmallCard
            title="QMS Documents"
            value={loading ? "—" : `${formatValue(dashboardData.qmsActive)} / ${formatValue(dashboardData.qmsArchived)}`}
            subtitle="Active / Archived"
            backgroundColor="#f8fafc"
            iconBgColor="#fef3c7"
            iconColor="#b45309"
            fontColor="#1e293b"
            height="110px"
            icon="fa fa-file-alt"
          />
        </div>
      </div>

      <div className="row g-3 align-items-stretch">
        <div className="col-md-6 d-flex">
          <NotificationCard
            notifications={notifications}
            onMarkAsRead={(id) =>
              setNotifications((prev) =>
                prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
              )
            }
          />
        </div>
        <div className="col-md-6 d-flex">
          <RecentActivityCard activities={recentActivity} />
        </div>
      </div>

    </div>
  );
}

export default Dashboard;
